import type { ReactNode, SVGProps } from 'react';

/**
 * Compact inline-SVG icon set (outline / stroke style, echoing the DS's Solar
 * "outline" guidance). Inlined instead of pulling `@iconify/react` so the mock
 * has zero runtime icon-fetch dependency. Size via `className` (e.g. `size-4`).
 */
type IconProps = SVGProps<SVGSVGElement>;

function Svg({ children, ...props }: IconProps & { children: ReactNode }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            {...props}
        >
            {children}
        </svg>
    );
}

export const IconOverview = (p: IconProps) => (
    <Svg {...p}>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Svg>
);

export const IconFlag = (p: IconProps) => (
    <Svg {...p}>
        <path d="M5 21V4" />
        <path d="M5 4h12l-2 3.5L17 11H5" />
    </Svg>
);

export const IconAddon = (p: IconProps) => (
    <Svg {...p}>
        <path d="M12 3l1.9 4.8L19 9.5l-5.1 1.7L12 16l-1.9-4.8L5 9.5l5.1-1.7L12 3z" />
    </Svg>
);

export const IconOrgs = (p: IconProps) => (
    <Svg {...p}>
        <rect x="4" y="3" width="9" height="18" rx="1.5" />
        <path d="M13 8h7v13h-7" />
        <path d="M7 7h2M7 11h2M7 15h2" />
    </Svg>
);

export const IconAudit = (p: IconProps) => (
    <Svg {...p}>
        <path d="M4 6h16M4 12h16M4 18h10" />
    </Svg>
);

export const IconAccess = (p: IconProps) => (
    <Svg {...p}>
        <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" />
    </Svg>
);

/** Feature Activation: a checklist whose last line is a padlock — features to switch on. */
export const IconActivation = (p: IconProps) => (
    <Svg {...p}>
        <path d="m3.5 5.5 1.5 1.5 2.5-3" />
        <path d="m3.5 12.5 1.5 1.5 2.5-3" />
        <path d="M11 6h9M11 13h4" />
        <rect x="14" y="17" width="7" height="5" rx="1" />
        <path d="M15.5 17v-1.5a2 2 0 0 1 4 0V17" />
        <path d="M4 20h6" />
    </Svg>
);

/** A warning: something the app could not find out, not something the user did wrong. */
export const IconWarning = (p: IconProps) => (
    <Svg {...p}>
        <path d="M10.3 4.2 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0z" />
        <path d="M12 9.5v4.5M12 17h.01" />
    </Svg>
);

export const IconSearch = (p: IconProps) => (
    <Svg {...p}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.2-3.2" />
    </Svg>
);

export const IconBell = (p: IconProps) => (
    <Svg {...p}>
        <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" />
        <path d="M10 20a2 2 0 0 0 4 0" />
    </Svg>
);

export const IconHelp = (p: IconProps) => (
    <Svg {...p}>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.4 2.3c-.8.4-1.4 1-1.4 1.9" />
        <path d="M12 17h.01" />
    </Svg>
);

export const IconChevronDown = (p: IconProps) => (
    <Svg {...p}>
        <path d="m6 9 6 6 6-6" />
    </Svg>
);

export const IconArrowRight = (p: IconProps) => (
    <Svg {...p}>
        <path d="M5 12h14M13 6l6 6-6 6" />
    </Svg>
);

export const IconArrowLeft = (p: IconProps) => (
    <Svg {...p}>
        <path d="M19 12H5M11 6l-6 6 6 6" />
    </Svg>
);

export const IconPlus = (p: IconProps) => (
    <Svg {...p}>
        <path d="M12 5v14M5 12h14" />
    </Svg>
);

export const IconTrendUp = (p: IconProps) => (
    <Svg {...p}>
        <path d="m4 16 5-5 3 3 7-7" />
        <path d="M16 7h4v4" />
    </Svg>
);

export const IconArchive = (p: IconProps) => (
    <Svg {...p}>
        <rect x="3" y="4" width="18" height="5" rx="1" />
        <path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" />
        <path d="M10 13h4" />
    </Svg>
);

export const IconExternal = (p: IconProps) => (
    <Svg {...p}>
        <path d="M14 4h6v6" />
        <path d="M20 4 10 14" />
        <path d="M20 14v6H4V4h6" />
    </Svg>
);

/*
 * The three theme states. Drawn as strokes on the same 24px grid as everything
 * above rather than borrowed from an icon font: the set is `currentColor` all
 * the way down, which is what lets the switch tint them from a token.
 */

export const IconSun = (p: IconProps) => (
    <Svg {...p}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </Svg>
);

export const IconMoon = (p: IconProps) => (
    <Svg {...p}>
        <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
    </Svg>
);

/** "Follow the operating system" — a display, not a half-moon. */
export const IconSystem = (p: IconProps) => (
    <Svg {...p}>
        <rect x="3" y="4" width="18" height="12" rx="1.5" />
        <path d="M9 20h6M12 16v4" />
    </Svg>
);
